import { Table, PromiseExtended } from 'dexie';
import { BaseModelForPush } from './base-model';
import { SyncRecordStatus } from '../types/sync-state';
import {
  IFlnAssessment,
  IFlnTask,
  FlnAssessmentRecord,
} from '../types/fln-assessment';

const SCHEMA_VERSION = 1;
const SCHEMA = {
  flnAssessments: '++id, gradeSubject, grade, subject',
};

export class FlnAssessmentDB extends BaseModelForPush {
  flnAssessments!: Table<FlnAssessmentRecord>;

  constructor() {
    super('flnAssessmentdb');
    this.version(SCHEMA_VERSION).stores(SCHEMA);
    this.flnAssessments = this.table('flnAssessments');
    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  getAssessmentByGradeSubject(
    gradeSubject: number,
  ): PromiseExtended<FlnAssessmentRecord | undefined> {
    return this.flnAssessments.where({ gradeSubject }).first();
  }

  getAllAssessments(): PromiseExtended<FlnAssessmentRecord[]> {
    return this.flnAssessments.toArray();
  }

  async getTasksByGradeSubject(gradeSubject: number): Promise<IFlnTask[]> {
    const assessment = await this.getAssessmentByGradeSubject(gradeSubject);
    return assessment?.tasks || [];
  }

  async addAssessment(
    assessmentData: IFlnAssessment & { tasks: IFlnTask[] },
  ): Promise<void> {
    try {
      // Check if assessment already exists
      const existing = await this.getAssessmentByGradeSubject(
        assessmentData.gradeSubject,
      );

      if (existing) {
        return;
      }

      const recordData: FlnAssessmentRecord = {
        ...assessmentData,
        syncStatus: SyncRecordStatus.Synced,
      };

      await this.flnAssessments.add(recordData);
    } catch (error) {
      console.error('Error adding FLN assessment:', error);
    }
  }

  // Required by BaseModelForPush but not used for FLN assessments
  async getAllUnsyncedRecords(): Promise<Record<string, any[]>> {
    return { flnAssessments: [] };
  }

  // Required by BaseModelForPush but not used for FLN assessments
  async updateRecordsSyncStatus(): Promise<number> {
    return 0;
  }
}

export const flnAssessmentDB = new FlnAssessmentDB();
